import { FormTypes } from '../../../../account-info/models/form-models';
import { BeneficiariesRequest, Beneficiary, beneficiaryContingentStatus, BeneficiaryData, BeneficiaryDto, BeneficiaryLandingPageModel, beneficiaryNotDesignatedStatus, BeneficiaryPerson, beneficiaryPrimaryStatus, BeneficiaryRelationshipData, beneficiarySpouseRelationship, BeneficiaryUpdatePageModel, BeneficiaryUpdatePageOptions, cpfLife, Designation, layLumpSum, PlanUpdate, PlanWithBeneficiary, preretirement } from '../model/beneficiary-models';
import { BeneficiarySettings } from '../model/epi-props';

export function GetMappedStatusByValue(status: string, beneficiarySettings: BeneficiarySettings) {
    switch (status) {
        case beneficiaryPrimaryStatus:
            return beneficiarySettings?.statusPrimaryLabel || 'Primary';

        case beneficiaryNotDesignatedStatus:
            return beneficiarySettings?.statusNotDesignatedLabel || 'Not Designated';

        case beneficiaryContingentStatus:
            return beneficiarySettings?.statusContingentLabel || 'Contingent';

        default:
            return status;
    }
}

export function GetBeneficiaryStatusPrimaryBen1Options(beneficiarySettings: BeneficiarySettings) {
    return [
        {
            value: beneficiaryNotDesignatedStatus,
            text: GetMappedStatusByValue(beneficiaryNotDesignatedStatus, beneficiarySettings),
        },
        {
            value: beneficiaryPrimaryStatus,
            text: GetMappedStatusByValue(beneficiaryPrimaryStatus, beneficiarySettings),
        },
    ];
}

export function GetBeneficiaryStatusPrimaryBen2Options(beneficiarySettings: BeneficiarySettings) {
    return [
        {
            value: beneficiaryPrimaryStatus,
            text: GetMappedStatusByValue(beneficiaryPrimaryStatus, beneficiarySettings),
        },
        {
            value: beneficiaryContingentStatus,
            text: GetMappedStatusByValue(beneficiaryContingentStatus, beneficiarySettings),
        },
    ];
}

export function GetBeneficiaryStatusContingentBen2Options(beneficiarySettings: BeneficiarySettings) {
    return [
        {
            value: beneficiaryPrimaryStatus,
            text: GetMappedStatusByValue(beneficiaryPrimaryStatus, beneficiarySettings),
        },
        {
            value: beneficiaryContingentStatus,
            text: GetMappedStatusByValue(beneficiaryContingentStatus, beneficiarySettings),
        },
    ];
}

export function GetMappedRelationshipByValue(relationshipId: string, mappedRelationship: any[]) {
    if (relationshipId && mappedRelationship?.length > 0) {
        return mappedRelationship.find(relationship => relationship?.value?.toLocaleLowerCase() === relationshipId?.toLocaleLowerCase())?.text || relationshipId;
    }

    return relationshipId;
}

export function getBeneficiaryPageModel(beneficiaryData: BeneficiaryData, showMessage: boolean): BeneficiaryLandingPageModel {
    return {
        showMessage,
        beneficiaryData,
        lastUpdatedTime: null,
    };
}

export function convertSpouseToBeneficiary(spouse: BeneficiaryPerson) {
    if (!spouse) {
        return null;
    }
    return {
        beneficiary_designation_type: null,
        percent: null,
        share_option: null,
        relationship_code: null,
        deleted: null,
        errors: null,
        designation_id: null,
        person: spouse,
    };
}

function parseIneligibleFor(value: string): string[] {
    return value?.toLocaleLowerCase()?.split(',');
}

function getBeneficiaryFromBeneficiaryDto(beneficiaryDto: BeneficiaryDto, beneficiaryRelationshipData: BeneficiaryRelationshipData[], defaultSpouseGuid: string): Beneficiary {
    if (!beneficiaryDto || beneficiaryDto?.deleted || !beneficiaryRelationshipData || beneficiaryRelationshipData?.length < 1) return null;

    // Fallback if we can find the information on the relationships data.
    const foundItemFromBeneficiary = beneficiaryRelationshipData.find(
        item =>
            (item?.person?.guid && item?.person?.guid === beneficiaryDto?.person?.guid) ||
            (item?.organization?.guid && item?.organization?.guid === beneficiaryDto?.organization?.guid));

    if (beneficiaryDto?.person?.guid) {
        return {
            id: beneficiaryDto?.person?.guid,
            firstName: beneficiaryDto?.person?.first_name || '',
            lastName: beneficiaryDto?.person?.last_name || '',
            dob: beneficiaryDto?.person?.date_of_birth || beneficiaryDto?.person?.masked_date_of_birth || '',
            relationship: beneficiaryDto?.relationship_code || '',
            status: beneficiaryDto.beneficiary_designation_type || '',
            participation: beneficiaryDto.percent,
            designationId: beneficiaryDto.designation_id,
            isOrganization: false,
            ineligibleFor: parseIneligibleFor(foundItemFromBeneficiary?.ineligible_for),
            isCurrentSpouse: defaultSpouseGuid && beneficiaryDto?.person?.guid && beneficiaryDto?.person?.guid === defaultSpouseGuid,
        };
    }

    if (beneficiaryDto?.organization?.guid) {
        return {
            id: beneficiaryDto?.organization?.guid,
            firstName: beneficiaryDto?.organization?.name || '',
            lastName: '',
            dob: '',
            relationship: beneficiaryDto?.relationship_code || '',
            status: beneficiaryDto.beneficiary_designation_type || '',
            participation: beneficiaryDto.percent,
            designationId: beneficiaryDto.designation_id,
            isOrganization: true,
            ineligibleFor: parseIneligibleFor(foundItemFromBeneficiary?.ineligible_for),
        };
    }

    if (!foundItemFromBeneficiary) return null;

    return {
        id: foundItemFromBeneficiary?.person?.guid || foundItemFromBeneficiary?.organization?.guid,
        firstName: foundItemFromBeneficiary?.person?.first_name || foundItemFromBeneficiary?.organization?.name || '',
        lastName: foundItemFromBeneficiary?.person?.last_name || '',
        dob: foundItemFromBeneficiary?.person?.date_of_birth || foundItemFromBeneficiary?.person?.masked_date_of_birth || '',
        relationship: foundItemFromBeneficiary?.relationship_code || '',
        status: beneficiaryDto.beneficiary_designation_type || '',
        participation: beneficiaryDto.percent,
        designationId: beneficiaryDto.designation_id,
        isOrganization: !foundItemFromBeneficiary?.person,
        ineligibleFor: parseIneligibleFor(foundItemFromBeneficiary?.ineligible_for),
        isCurrentSpouse: defaultSpouseGuid && foundItemFromBeneficiary?.person?.guid && foundItemFromBeneficiary?.person?.guid === defaultSpouseGuid,
    };
}

function getDesignationFromBeneficiary(beneficiary: Beneficiary, employeeGuid: string, beneficiaryDesignationType: string): Designation {
    if (!beneficiary) return null;

    const designation: Designation = {
        employee_guid: employeeGuid,
        beneficiary_designation_type: beneficiaryDesignationType,
        id: beneficiary?.designationId?.toString() || '',
        percent: (beneficiary?.participation === 0 || !beneficiary?.participation) ? null : Number(beneficiary?.participation),
    };

    if (beneficiary?.isOrganization) {
        designation.organization_guid = beneficiary?.id;
    }

    if (!beneficiary?.isOrganization) {
        designation.person_guid = beneficiary?.id;
    }

    return designation;
}

function getBeneficiaryFromBeneficiaryRelationshipData(item: BeneficiaryRelationshipData, defaultSpouseGuid: string): Beneficiary {
    return {
        id: item?.person?.guid || item?.organization?.guid,
        firstName: item?.person?.first_name || item?.organization?.name || '',
        lastName: item?.person?.last_name || '',
        dob: item?.person?.date_of_birth || item?.person?.masked_date_of_birth || '',
        relationship: item?.relationship_code || '',
        status: '',
        participation: 0,
        isOrganization: !item?.person,
        ineligibleFor: parseIneligibleFor(item?.ineligible_for),
        isCurrentSpouse: defaultSpouseGuid && item?.person?.guid && item?.person?.guid === defaultSpouseGuid,
    };
}

function getDefaultBeneficiary(plan: string, beneficiaryUpdatePageOptions: BeneficiaryUpdatePageOptions, default_spouse: BeneficiaryPerson, beneficiaryRelationshipData: BeneficiaryRelationshipData[]): Beneficiary {

    // Add spouse as not designated;
    if (plan === preretirement && !beneficiaryUpdatePageOptions?.isPrimaryBeneficiaryEditableWhenMarried && default_spouse?.masked_client_number) {
        return {
            id: default_spouse?.guid,
            firstName: default_spouse?.first_name || '',
            lastName: default_spouse?.last_name || '',
            dob: default_spouse?.date_of_birth || default_spouse?.masked_date_of_birth || '',
            relationship: beneficiarySpouseRelationship,
            status: beneficiaryNotDesignatedStatus,
            participation: 100,
            isOrganization: false,
            isCurrentSpouse: true,
        };
    }

    if (plan === layLumpSum) {
        /**
         * If there is no named beneficiary, the Death Benefit Plan provides that the following will be the default beneficiaries:
         *  3.32.1 Spouse
         *  3.32.2 If not married, children, or 
         *  3.32.3 If not married and had no children, then estate.
         */

        if (default_spouse) {
            return {
                id: default_spouse?.guid,
                firstName: default_spouse?.first_name || '',
                lastName: default_spouse?.last_name || '',
                dob: default_spouse?.date_of_birth || default_spouse?.masked_date_of_birth || '',
                relationship: beneficiarySpouseRelationship,
                status: beneficiaryNotDesignatedStatus,
                participation: 100,
                isOrganization: false,
                isCurrentSpouse: true,
            };
        }

        const foundChild = beneficiaryRelationshipData?.find(benficiary => benficiary.relationship_code === 'Child');

        if (foundChild) {
            return {
                ...getBeneficiaryFromBeneficiaryRelationshipData(foundChild, null),
                status: beneficiaryPrimaryStatus,
                participation: 100,
            };
        }

        const foundBeneficiary = beneficiaryRelationshipData?.find(benficiary => benficiary.relationship_code === 'Beneficiary');

        if (foundBeneficiary) {
            return {
                ...getBeneficiaryFromBeneficiaryRelationshipData(foundBeneficiary, null),
                status: beneficiaryPrimaryStatus,
                participation: 100,
            };
        }
    }

    return null;
}

export function convertBeneficiaryDataToPageViewModel(
    plan: string,
    planWithBeneficiary: PlanWithBeneficiary,
    beneficiaryRelationshipData: BeneficiaryRelationshipData[],
    default_spouse: BeneficiaryPerson,
    beneficiaryUpdatePageOptions: BeneficiaryUpdatePageOptions): BeneficiaryUpdatePageModel {
    if (!planWithBeneficiary || !beneficiaryRelationshipData) {
        return null;
    }

    const primaryBeneficiaries: Beneficiary[] = [];
    const contingentBeneficiaries: Beneficiary[] = [];
    const clientBeneficiaries: Beneficiary[] = [];
    const defaultSpouseGuid: string = default_spouse?.guid;

    planWithBeneficiary.primary?.forEach(
        beneficiaryDto => {
            const convertedBeneficiary = getBeneficiaryFromBeneficiaryDto(beneficiaryDto, beneficiaryRelationshipData, defaultSpouseGuid);
            if (convertedBeneficiary && !primaryBeneficiaries.find(item => item.id === convertedBeneficiary.id)) {
                primaryBeneficiaries.push(convertedBeneficiary);
            }
        });

    planWithBeneficiary.contingent?.forEach(
        beneficiaryDto => {
            const convertedBeneficiary = getBeneficiaryFromBeneficiaryDto(beneficiaryDto, beneficiaryRelationshipData, defaultSpouseGuid);
            if (convertedBeneficiary && !contingentBeneficiaries.find(item => item.id === convertedBeneficiary.id)) {
                contingentBeneficiaries.push(convertedBeneficiary);
            }
        });

    beneficiaryRelationshipData?.forEach(
        beneficiaryRelationshipDataItem => {
            const convertedBeneficiary = getBeneficiaryFromBeneficiaryRelationshipData(beneficiaryRelationshipDataItem, defaultSpouseGuid);
            if (convertedBeneficiary) {
                clientBeneficiaries.push(convertedBeneficiary);
            }
        });

    if (primaryBeneficiaries?.length === 1 && primaryBeneficiaries[0].participation !== 100) {
        primaryBeneficiaries[0].participation = 100;
    }

    if (primaryBeneficiaries?.length === 0) {
        const defaultBeneficiary = getDefaultBeneficiary(plan, beneficiaryUpdatePageOptions, default_spouse, beneficiaryRelationshipData);

        if (defaultBeneficiary) {
            primaryBeneficiaries.push(defaultBeneficiary);
        }
    }

    const model: BeneficiaryUpdatePageModel = {
        primaryBeneficiaries,
        contingentBeneficiaries,
        clientBeneficiaries,
        legalCheck: false,
        legalCheck2: false,
        primaryShareOption: planWithBeneficiary.primary[0]?.share_option || '',
        contingentShareOption: planWithBeneficiary.contingent[0]?.share_option,
        isMarried: default_spouse?.masked_client_number?.length > 0,
    };

    return model;
}

export function getBeneficiaryUpdatePageOptionsByType(plan: string, beneficiarySettings: BeneficiarySettings, default_spouse: BeneficiaryPerson): BeneficiaryUpdatePageOptions {

    const isMarried = default_spouse?.masked_client_number?.length > 0;

    switch (plan) {
        case cpfLife:
            return {
                planTitle: beneficiarySettings?.cpfPlanName || 'Clergy DB CPF Life Benefit',
                planDescription: beneficiarySettings?.cpfPlanDescription,
                beneficiaryStatusPrimaryOptions: GetBeneficiaryStatusPrimaryBen2Options(beneficiarySettings),
                beneficiaryStatusContingentOptions: GetBeneficiaryStatusContingentBen2Options(beneficiarySettings),
                isPrimaryBeneficiaryEditableWhenMarried: true,
                shareOptionPrimaryMin: 1,
                shareOptionContingentMin: 1,
                guidelineMessage: beneficiarySettings?.cpfPlanGuidelinesMessage,
                guidelineCheckboxMessage: beneficiarySettings?.cpfPlanGuidelinesCheckboxMessage,
                legalCheckText1: beneficiarySettings?.cpfPlanLegalMessage1,
                legalCheckText2: beneficiarySettings?.cpfPlanLegalMessage2,
                hasPercentages: true,
            };

        case preretirement:
            return {
                planTitle: beneficiarySettings?.dbPlanName || 'Clergy DB Preretirement Survivor Benefit',
                planDescription: beneficiarySettings?.dbPlanDescription,
                beneficiaryStatusPrimaryOptions: isMarried ? GetBeneficiaryStatusPrimaryBen1Options(beneficiarySettings) : GetBeneficiaryStatusPrimaryBen2Options(beneficiarySettings),
                beneficiaryStatusContingentOptions: isMarried ? null : GetBeneficiaryStatusContingentBen2Options(beneficiarySettings),
                isPrimaryBeneficiaryEditableWhenMarried: false,
                shareOptionPrimaryMin: -1,
                shareOptionContingentMin: -1,
                guidelineMessage: beneficiarySettings?.dbPlanGuidelinesMessage,
                guidelineCheckboxMessage: beneficiarySettings?.dbPlanGuidelinesCheckboxMessage,
                legalCheckText1: beneficiarySettings?.dbPlanLegalMessage1,
                legalCheckText2: beneficiarySettings?.dbPlanLegalMessage2,
                legalCheck2OnlyWhenContingent: true,
                hasPercentages: false,
            };

        case layLumpSum:
            return {
                planTitle: beneficiarySettings?.layLumpSumPlanName || 'Lay Lump-Sum Death Benefit',
                planDescription: beneficiarySettings?.layLumpSumPlanDescription,
                beneficiaryStatusPrimaryOptions: GetBeneficiaryStatusPrimaryBen2Options(beneficiarySettings),
                beneficiaryStatusContingentOptions: GetBeneficiaryStatusContingentBen2Options(beneficiarySettings),
                isPrimaryBeneficiaryEditableWhenMarried: true,
                shareOptionPrimaryMin: -1,
                shareOptionContingentMin: -1,
                guidelineMessage: beneficiarySettings?.layLumpSumPlanGuidelinesMessage,
                guidelineCheckboxMessage: beneficiarySettings?.layLumpSumPlanGuidelinesCheckboxMessage,
                legalCheckText1: beneficiarySettings?.layLumpSumPlanLegalMessage1,
                legalCheckText2: beneficiarySettings?.layLumpSumPlanLegalMessage2,
                hasPercentages: true,
            };

        default:
            return null;
    }
}

function RemoveParticipation(beneficiaries: Designation[], beneficiaryDesignationType: string): Designation[] {

    if (!beneficiaries || !beneficiaryDesignationType) return beneficiaries;

    const newBeneficiaries: Designation[] = [];

    beneficiaries.forEach(beneficiary => {

        const newBeneficiary = {
            ...beneficiary,
        };

        delete newBeneficiary.percent;

        newBeneficiaries.push(newBeneficiary);
    });

    return newBeneficiaries;
}

export function DetectBeneficiaryChanges(initialValue: BeneficiaryUpdatePageModel, submitValues: BeneficiaryUpdatePageModel, bentype: string, employeeGuid: string, shareOptionPrimaryMin: number, shareOptionContingentMin: number, hasPercentages: boolean): BeneficiariesRequest {
    const request: BeneficiariesRequest = {
        beneficiaries: {},
    };

    let designations: Designation[] = [];

    submitValues.primaryBeneficiaries?.forEach(
        beneficiary => {
            const convertedDesignation = getDesignationFromBeneficiary(beneficiary, employeeGuid, beneficiaryPrimaryStatus);
            if (convertedDesignation) {
                designations.push(convertedDesignation);
            }
        });

    submitValues.contingentBeneficiaries?.forEach(
        beneficiary => {
            const convertedDesignation = getDesignationFromBeneficiary(beneficiary, employeeGuid, beneficiaryContingentStatus);
            if (convertedDesignation) {
                designations.push(convertedDesignation);
            }
        });

    if (!hasPercentages) {
        designations = RemoveParticipation(designations, beneficiaryPrimaryStatus);
        designations = RemoveParticipation(designations, beneficiaryContingentStatus);
    }

    // Add beneficiaries that need to be markes as deleted.
    initialValue?.primaryBeneficiaries.forEach(
        beneficiary => {
            const includedBeneficiary = designations.find(designation => (designation.person_guid && designation.person_guid === beneficiary.id) || (designation.organization_guid && designation.organization_guid === beneficiary.id));

            if (!includedBeneficiary) {
                const convertedDesignation = getDesignationFromBeneficiary(beneficiary, employeeGuid, beneficiaryPrimaryStatus);
                if (convertedDesignation) {
                    designations.push({
                        ...convertedDesignation,
                        deleted: 'Y',
                    });
                }
            }
        });

    initialValue?.contingentBeneficiaries.forEach(
        beneficiary => {
            const includedBeneficiary = designations.find(designation => (designation.person_guid && designation.person_guid === beneficiary.id) || (designation.organization_guid && designation.organization_guid === beneficiary.id));

            if (!includedBeneficiary) {
                const convertedDesignation = getDesignationFromBeneficiary(beneficiary, employeeGuid, beneficiaryContingentStatus);
                if (convertedDesignation) {
                    designations.push({
                        ...convertedDesignation,
                        deleted: 'Y',
                    });
                }
            }
        });

    const planUpdate: PlanUpdate = {
        designations,
    };

    if (shareOptionPrimaryMin > -1 && submitValues.primaryBeneficiaries?.length >= shareOptionPrimaryMin) {
        planUpdate.primary_share_option = submitValues.primaryShareOption;
    }

    if (shareOptionContingentMin > -1 && submitValues.contingentBeneficiaries?.length >= shareOptionContingentMin) {
        planUpdate.contingent_share_option = submitValues.contingentShareOption;
    }

    request.beneficiaries[bentype] = planUpdate;

    return request;
}

export function FormTypeByPlan(plan: string): FormTypes {
    switch (plan) {
        case cpfLife:
            return FormTypes.BENEFICIARY_UPDATE_CPF_LIFE;

        case preretirement:
            return FormTypes.BENEFICIARY_UPDATE_PRERETIREMENT;

        case layLumpSum:
            return FormTypes.BENEFICIARY_UPDATE_LAY_LUMP_SUM;

        default:
            return null;
    }
}

export function GetErrorFromPlan(planResponse): string[] {

    const itemErrors = [];

    if (planResponse?.primary?.length > 0) {
        planResponse.primary.forEach(beneficiary => {
            if (beneficiary.errors?.base?.length > 0 && beneficiary.errors?.base[0] && !itemErrors.includes(beneficiary.errors?.base[0])) {
                itemErrors.push(beneficiary.errors?.base[0]);
            }
        });
    }

    if (planResponse?.contingent?.length > 0) {
        planResponse.contingent.forEach(beneficiary => {
            if (beneficiary.errors?.base?.length && beneficiary.errors?.base[0] && !itemErrors.includes(beneficiary.errors?.base[0])) {
                itemErrors.push(beneficiary.errors?.base[0]);
            }
        });
    }

    return itemErrors.length > 0 ? itemErrors : null;
}

export function filterBeneficiaryArrayToHaveUnique(arr: BeneficiaryDto[]): any {
    const uniqueSet = new Set();
    const result: any = [];

    arr.forEach((item) => {
        let guid = '';
        if (item?.person?.guid) {
            guid = item?.person?.guid;
        }

        if (item?.organization?.guid) {
            guid = item?.organization?.guid;
        }

        if (guid !== '' && !uniqueSet.has(item?.person?.guid)) {
            uniqueSet.add(guid);
            result.push(item);
        }
    });

    return result;
}